import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { DatePicker, Row, Col, Form, Steps, Image, Radio, Checkbox, Typography, Tooltip } from "antd";
import toast from "@comps/toast";
import { InputText, CancelButton, SaveButton, ButtonComponent, UploadFile, Select, useConfirm } from "@comps/components";
import { fileUrl } from "@utils/axios";
import { getErrorProps } from "@utils/helpers";
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import { getCaseDependenciesReq, deleteCaseFileReq, getCompleteCaseDataReq, getEligibleOwnersReq } from "../requests";
import TextArea from "antd/es/input/TextArea";
import ScanImage from "@assets/images/icons/stl.png";
import { MEDIA_FORMATS } from "@utils/mediaFormats";
import { Icon } from '@iconify/react';
import dayjs from "dayjs";

const { Text } = Typography;

// ---------------------------------------------------------------------
// Small presentational helpers (pure layout/styling — no business logic)
// ---------------------------------------------------------------------

const SectionCard = ({ icon, title, subtitle, children, style }) => (
  <div style={ {
    background: '#fff',
    border: '1px solid #eef0f2',
    borderRadius: 14,
    padding: '22px 24px',
    marginBottom: 20,
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.03)',
    ...style,
  } }>
    <div style={ { display: 'flex', alignItems: 'center', gap: 10, marginBottom: subtitle ? 2 : 18 } }>
      { icon &&
        <span style={ {
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 9,
          background: '#eef4ff', color: '#1677ff', flexShrink: 0,
        } }>
          <Icon icon={ icon } width={ 18 } />
        </span>
      }
      <Text strong style={ { fontSize: 15 } }>{ title }</Text>
    </div>
    { subtitle && <Text type="secondary" style={ { display: 'block', fontSize: 12, marginBottom: 18 } }>{ subtitle }</Text> }
    { children }
  </div>
);

/**
 * Which step each field lives on, so a failed submit can take the
 * reader to it. Only fields that can actually fail need an entry.
 */
const STEP_OF_FIELD = {
  refine_id: 0, name: 0, case_type: 0, arch_side: 0, gender: 0, dob: 0,
  belongs_to_id: 0, clinic_id: 0,
  doctor_instructions: 1,
};

const clinicalFields = [
  { name: 'overjet', label: 'Overjet' },
  { name: 'overbite', label: 'Overbite' },
  { name: 'crossbite', label: 'Crossbite' },
  { name: 'openbite', label: 'Openbite' },
  { name: 'midline', label: 'Midline' },
];

// Two checkboxes acting as one exclusive choice: ticking one clears the
// other, and unticking the active one clears the field. Passed `value`/
// `onChange` the way Form.Item wires up any controlled field.
const ImproveMaintainCheckboxes = ({ value, onChange }) => (
  <div style={ { display: 'flex', gap: 20 } }>
    <Checkbox
      checked={ value === 'Improve' }
      onChange={ (e) => onChange?.(e.target.checked ? 'Improve' : null) }
    >
      Improve
    </Checkbox>
    <Checkbox
      checked={ value === 'Maintain' }
      onChange={ (e) => onChange?.(e.target.checked ? 'Maintain' : null) }
    >
      Maintain
    </Checkbox>
  </div>
);

// Treatment is only offered to patients born up to the end of 2020, so
// the picker stops at 2020-12-31 and anything from January 2021 on is
// rejected — the calendar blocks it, the rule below catches a typed date.
const DOB_LIMIT = dayjs('2021-01-01');
const isAfterDobLimit = (value) => !!value && !dayjs(value).isBefore(DOB_LIMIT, 'day');

// The clinical answers are stored as strings. Older cases carry the
// "Yes"/"No"/"maintain" the original three-way radios wrote, and cases
// saved under the single-checkbox UI carry "Yes"/null — both are read
// back as "Improve" here, since that is what either previously meant.
// An unanswered field stays null rather than "No" for the doctor.
const toClinicalValue = (stored) => {
  if (stored === 'Improve' || stored === 'Yes') return 'Improve';
  if (stored === 'Maintain') return 'Maintain';
  return null;
};
const fromClinicalValue = (value) => (value === 'Improve' || value === 'Maintain' ? value : null);

// Control height belongs to the antd theme (token controlHeight), not to
// individual fields — pinning selects to 50px left them a step taller than
// the text inputs beside them.
const fullWidth = { width: '100%' };

// `loader` is the submit flag and nothing else reads it — every other
// request on this screen now reports in the control it belongs to, so the
// footer only ever says "Working…" for an actual save.
const CaseJobForm = ({ onFinish, initialData, formName, errors, setErrors, onClosed, setPhotos, setXray, setScans, loader, caseFiles, reloadCaseFiles }) => {
  const authUser = useSelector((state) => state.auth.authUser);
  // Only an admin can decide who a case belongs to. Everyone else's case
  // is always their own — the backend already defaults belongs_to_id to
  // the creator when it isn't sent, so we simply don't show the field.
  // is_admin (from login) reflects user.is_system OR role.is_system —
  // NOT a hardcoded role_id, which differs per seeded role (e.g. System
  // Admin=1 vs Super Admin=2) and silently missed admins like the latter.
  const canAssignOwner = !!authUser?.is_admin;

  const [form] = Form.useForm();
  const confirm = useConfirm();
  const [types, setTypes] = useState([]);
  const [owners, setOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  // A case already marked as a refinement has to open as one, or the
  // edit drawer showed "Yes, new case" over a case whose reference was
  // set, and saving quietly dropped it.
  const [newCase, setNewCase] = useState(!(initialData && initialData.refine_id));
  const [currentStep, setCurrentStep] = useState(0);
  const [showAll, setShowAll] = useState(false);

  /*
   * A list, and empty until it is fetched.
   *
   * This was initialised to {}, and the Reference Case select below is
   * rendered whenever the case being edited already carries a refine_id
   * — which every on-hold case does, the list marks them by it. So
   * opening Edit on a held case handed rc-select an object to flatten
   * with forEach, and the whole app went blank on the click.
   */
  const [completeCaseData, setCompleteCaseData] = useState([]);
  // Scoped to the Reference Case select, deliberately: this is the only
  // control the request feeds, so it is the only one that should look
  // busy while it runs.
  const [refCasesLoading, setRefCasesLoading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Attachments are fetched by UpdateCase before the drawer opens, so the
  // form never loads them itself — see the note there.
  const caseData = caseFiles || {};

  // "Which Arch" (upper/lower) only applies when the selected case type
  // is the single-arch one — case type already carries that meaning, so
  // there's no separate "Arch Type" field.
  const selectedCaseType = Form.useWatch('case_type', form);
  const isSingleArch = types.find(t => t.value === selectedCaseType)?.key === 'singlearch';

  useEffect(() => {
    getDependencies();
    if (canAssignOwner) {
      getEligibleOwners();
    }
  }, []);

  useEffect(() => {
    if (currentStep === 3) {
      setShowAll(true);
    }
  }, [currentStep]);

  // The stored strings are turned into checked/unchecked here and back
  // again on submit, so neither CreateCase nor UpdateCase has to know the
  // clinical answers are kept as text.
  const formInitialValues = useMemo(() => {
    if (!initialData) return initialData;
    const clinical = {};
    clinicalFields.forEach(({ name }) => { clinical[name] = toClinicalValue(initialData[name]); });
    // dob arrives from the API as a plain "YYYY-MM-DD" string; DatePicker
    // only understands a dayjs value.
    const dob = initialData.dob ? dayjs(initialData.dob) : undefined;
    // A select matches its options by strict equality, so the owner id
    // has to be the same type here as it is in ownerOptions below — a
    // "2" against a 2 would leave the field looking unset (or, before
    // that list carried the assigned owner at all, showing the bare id).
    const belongs_to_id = initialData.belongs_to_id
      ? Number(initialData.belongs_to_id)
      : initialData.belongs_to_id;
    return { ...initialData, ...clinical, dob, belongs_to_id };
  }, [initialData]);

  /**
   * Why Save did nothing.
   *
   * Every step stays mounted (the ones you are not on are display:none),
   * so validation covers the whole form — but a field that fails on a
   * hidden step cannot be seen, cannot be scrolled to, and antd reports
   * it only by marking the field. Pressing Save on the Review step with,
   * say, no arch chosen was therefore completely silent.
   *
   * So: show the first thing that is wrong, and take the reader to the
   * step it is on so the highlighted field is actually on screen.
   */
  const handleFinishFailed = ({ errorFields }) => {
    if (!errorFields || errorFields.length === 0) return;

    const [first] = errorFields;
    const fieldName = Array.isArray(first.name) ? first.name[0] : first.name;
    const step = STEP_OF_FIELD[fieldName];

    if (step !== undefined && step !== currentStep) {
      setCurrentStep(step);
    }

    toast.error(
      errorFields.length === 1 ? "One field still needs attention" : `${errorFields.length} fields still need attention`,
      first.errors[0]
    );
  }

  const handleFinish = (values) => {
    const clinical = {};
    clinicalFields.forEach(({ name }) => { clinical[name] = fromClinicalValue(values[name]); });

    // Editing an existing case no longer offers a reference-case picker —
    // marking it "It's a refinement" sends it round again as-is. If it
    // already carries a genuine reference to a different case, that
    // reference is resent unchanged (the backend treats re-sending the
    // same reference as a no-op, not a second round); otherwise the
    // case's own id is sent, which the backend recognises as "do this one
    // again" and converts into a refinement round.
    const refinement = (!newCase && initialData)
      ? {
        refine_id: (initialData.refine_id && Number(initialData.refine_id) !== Number(initialData.id))
          ? initialData.refine_id
          : initialData.id,
      }
      : {};

    onFinish({ ...values, ...clinical, ...refinement });
  };

  /*
   * Enter does what the primary button on the current step does: Next on
   * the first three steps, Create on Review. Left to the browser it did
   * nothing until Review — Save is the form's only submit button and it
   * isn't rendered before then.
   *
   * Controls that already use Enter for themselves keep it: a newline in
   * the instructions, a Select opening or picking (it marks the event
   * handled), a date being committed, an upload opening its file dialog,
   * a focused button clicking itself.
   *
   * Listened for on the window, not the form: after an upload or a click
   * on Next, focus is on a button or nowhere at all, and Enter on the
   * Photos/Scans and Review steps would never have reached the form.
   * Anything focused outside this drawer — a dialog on top, the page
   * behind — is left alone.
   */
  useEffect(() => {
    const handleEnter = (e) => {
      if (e.key !== 'Enter' || e.shiftKey || e.defaultPrevented || e.isComposing) return;
      const { target } = e;
      const drawer = document.getElementById(formName)?.closest('.ant-drawer');
      if (target !== document.body && !(drawer && drawer.contains(target))) return;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON'
        || target.closest('.ant-picker, .ant-upload, [role="button"]')) return;

      e.preventDefault();
      if (loader) return;
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        form.submit();
      }
    };

    window.addEventListener('keydown', handleEnter);
    return () => window.removeEventListener('keydown', handleEnter);
  }, [currentStep, loader, form, formName]);

  /*
   * Deleting an attachment is that file's business, not the form's — it
   * used to run through the submit loader, so removing one photo put
   * "Working…" on Next and Save as though the whole case were being
   * submitted. The one trash icon that was clicked reports instead.
   */
  const deleteFile = (id, type) => {
    setDeletingFileId(id);
    makeRequest(
      // makeRequest drives a boolean; what this screen needs to know is
      // *which* file is going, so only the closing call is used.
      (busy) => { if (!busy) setDeletingFileId(null); },
      deleteCaseFileReq, { id, type }, onDeleteSuccess, onError);
  }

  /**
   * Deleting an attachment used to ask "Are you sure?" from a bubble that
   * named neither the file nor the section it was in. Name both.
   */
  const askThenDeleteFile = async (file, section, type) => {
    const answer = await confirm({
      tone: 'danger',
      icon: 'tabler:trash',
      title: `Delete this ${section.toLowerCase()} file?`,
      description: file.name ? `"${file.name}" will be removed from this case.` : undefined,
      outcome: 'Deleting is permanent — you would have to upload it again.',
      confirmText: 'Delete',
      cancelText: 'Keep it',
    });

    if (answer) deleteFile(file.id, type);
  }

  const onDeleteSuccess = (res) => {
    if (reloadCaseFiles) reloadCaseFiles();
    toast.success("File deleted successfully");
  }

  /*
   * Deliberately stateless. These two feed the Case Type and Assign To
   * selects on mount; routing them through setLoader put "Working..." on
   * the footer buttons the moment the form opened, which read as a
   * submit already in flight.
   */
  const getDependencies = () => {
    makeRequestStateless(getCaseDependenciesReq, null, onSuccess, onError);
  }

  const onSuccess = (res) => {
    setTypes(res.all_types);
  }

  const getEligibleOwners = () => {
    setOwnersLoading(true);
    makeRequestStateless(getEligibleOwnersReq, null, onOwnersSuccess,
      (err) => { setOwnersLoading(false); onError(err); });
  }

  const onOwnersSuccess = (res) => {
    setOwners(Array.isArray(res) ? res : []);
    setOwnersLoading(false);
  }

  /**
   * The Assign Case To list, with whoever the case is assigned to
   * already in it.
   *
   * The endpoint answers with *eligible* owners — doctors, and never an
   * admin, since a case is not meant to be owned by one. But a case an
   * admin raises without picking a doctor belongs to that admin (the API
   * defaults the owner to the creator), and a doctor can be deactivated
   * or have their role changed after the fact. In all of those the
   * stored owner is missing from the options, and a select whose value
   * matches no option falls back to printing the raw value — which is
   * why this field read "2" rather than naming anybody.
   *
   * The case row already carries the owner, so name them from it. The
   * entry is only added when it is genuinely absent, so it never
   * duplicates a doctor who is on the list.
   */
  const ownerOptions = useMemo(() => {
    const assigned = initialData && initialData.belongs_to_id;
    if (! assigned) {
      return owners;
    }

    const known = owners.some((each) => Number(each.value) === Number(assigned));
    if (known) {
      return owners;
    }

    const owner = initialData.belongs_to_user || initialData.created_by_user;
    return [
      {
        value: Number(assigned),
        // Better a name that is only in the list because it is already
        // on the case than a bare id — and if even the name did not come
        // back with the row, say plainly that this is an account id.
        label: (owner && owner.name) || `User #${assigned}`,
      },
      ...owners,
    ];
  }, [owners, initialData]);

  const onError = (error) => {
    console.log(error);
  }

  const onUploadPhoto = (file) => {
    let files = [];
    file && file.map((f) => {
      files.push(f.originFileObj);
    });
    setPhotos(files);
  }

  const onUploadXray = (file) => {
    let files = [];
    file && file.map((f) => {
      files.push(f.originFileObj);
    });
    setXray(files);
  }

  const onUploadScan = (file) => {
    let files = [];
    file && file.map((f) => {
      files.push(f.originFileObj);
    });
    setScans(files);
  }

  /*
   * Picking "It's a refinement" reveals the Reference Case select when
   * creating a new case row for a refinement of a different case — that
   * select's options have to be fetched. This used to wait on one request
   * routed through the form's submit loader, so a click on the radio did
   * nothing visible for a moment while Back, Next and Save all switched to
   * "Working…" — the form looked like it was submitting a case the user
   * had not finished filling in.
   *
   * The radio answers straight away now, and the list loads inside the
   * one control that needs it. Editing an existing case never shows this
   * select at all (see the render below), so nothing is fetched for it.
   */
  const onChangeCaseType = (value) => {
    setNewCase(value);

    // A new case has no reference to pick; editing an existing one no
    // longer shows the picker either; and the list is the same one every
    // time either way — no reason to fetch it twice.
    if (value || initialData || refCasesLoading || completeCaseData.length) return;

    setRefCasesLoading(true);
    makeRequestStateless(getCompleteCaseDataReq, initialData && initialData.id,
      (res) => { setCompleteCaseData(Array.isArray(res) ? res : []); setRefCasesLoading(false); },
      (err) => { setRefCasesLoading(false); onError(err); });
  }

  // Renders one of the Photos / X-ray / Scans upload sections — the three
  // blocks were near-identical, so the shared layout lives here once.
  const renderMediaSection = ({ title, icon, files, image, onUpload, onDelete, type, uploadText }) => (
    <SectionCard icon={ icon } title={ title } style={ { marginBottom: 18 } }>
      <div className={ `media-grid ${files && files.length > 0 ? 'has-files' : ''}` }>
        { files && files.length > 0 && files.map((each) => (
          <div key={ each.id } className="media-thumb">
            <div style={ { padding: 10 } }>
              { image === 'scan'
                ? <img style={ { height: '80px', width: '80px' } } src={ ScanImage } />
                : <Image style={ { height: '80px', width: '80px' } } src={ fileUrl(each.path) } />
              }
              <div style={ { textAlign: 'center' } }>
                { deletingFileId === each.id
                  ? <Icon width={ 20 } icon="tabler:loader-2" className="spin"
                    style={ { color: 'var(--ink-faint)' } } />
                  : <Tooltip title="Delete">
                    <Icon
                      style={ { color: 'var(--danger)', cursor: 'pointer' } } width={ 20 } icon="tabler:trash"
                      onClick={ () => askThenDeleteFile(each, title, type) }
                    />
                  </Tooltip> }
              </div>
            </div>
          </div>
        )) }

        <div>
          <UploadFile
            hideBorder={ !!(files && files.length > 0) }
            multiple={ true }
            onUpload={ onUpload }
            listType="picture-card"
            uploadText={ uploadText }
            accept={ MEDIA_FORMATS[type].accept }
            extensions={ MEDIA_FORMATS[type].extensions }
            formatLabel={ MEDIA_FORMATS[type].label }
            showUploadList={ { showPreviewIcon: MEDIA_FORMATS[type].previewable } }
          />
        </div>
      </div>
    </SectionCard>
  );

  const stepItems = [
    { title: 'Primary Detail', icon: 'tabler:user' },
    { title: 'Doctor’s Instructions', icon: 'tabler:stethoscope' },
    { title: 'Photos/Scans', icon: 'tabler:camera' },
    { title: 'Review', icon: 'tabler:checklist' },
  ];

  return (
    <>
      <div style={ { marginBottom: 24 } }>
        {/* Each step's icon was declared above and then dropped on the
            way in, so the rail showed bare numbers 1-4 and neither the
            patient step nor the clinical one carried the mark its
            section headings use. */}
        <Steps
          size="small"
          current={ currentStep }
          onChange={ (step) => setCurrentStep(step) }
          items={ stepItems.map((step) => ({
            title: step.title,
            icon: <Icon icon={ step.icon } width={ 20 } />,
          })) }
        />
      </div>

      <Form
        form={ form }
        layout="vertical"
        name={ formName }
        onFinish={ handleFinish }
        onFinishFailed={ handleFinishFailed }
        initialValues={ formInitialValues }
        scrollToFirstError={ { behavior: 'smooth', block: 'center' } }
      >
        <div style={ {
          display: currentStep === 0 || showAll ? 'block' : 'none'
        } }>
          <SectionCard icon="tabler:clipboard-list" title="Case Details" subtitle="Tell us what kind of case this is">
            <Form.Item onChange={
              () => setErrors([])
            } label="Case Type :" className="da-mb-16"
              { ...getErrorProps(errors['name']) }
            >
              <Radio.Group
                buttonStyle="solid"
                value={ newCase }
                onChange={ (e) => onChangeCaseType(e.target.value) }
              >
                <Radio.Button value={ true }>Yes, new case</Radio.Button>
                <Radio.Button value={ false }>It’s a refinement</Radio.Button>
              </Radio.Group>
            </Form.Item>

            { !newCase && !initialData &&
              <Form.Item name="refine_id" onChange={
                () => setErrors([])
              } rules={ rules.refine_id } label="Reference Case :" className="da-mb-16"
                { ...getErrorProps(errors['refine_id']) }
              >
                <Select
                  options={ refCasesLoading ? [] : completeCaseData }
                  loading={ refCasesLoading }
                  disabled={ refCasesLoading }
                  placeholder={ refCasesLoading ? "Loading cases…" : "Select Reference Case" }
                  notFoundContent={ refCasesLoading ? null : undefined }
                />
              </Form.Item>
            }

            { !newCase && initialData &&
              <Text type="secondary" style={ { display: 'block', marginBottom: 16 } }>
                Saving will send this case back to Refinements.
              </Text>
            }

            <Row gutter={ 20 }>
              <Col xs={ 24 } md={ 12 }>
                <Form.Item onChange={
                  () => setErrors([])
                } name="name" rules={ rules.name } label="Patient Name :" className="da-mb-16"
                  { ...getErrorProps(errors['name']) }
                >
                  <InputText placeholder="Enter your patient name" />
                </Form.Item>
              </Col>
              <Col xs={ 24 } md={ 12 }>
                <Form.Item onChange={
                  () => setErrors([])
                } name="case_type" rules={ rules.case_type } label="Case Type :" className="da-mb-16"
                  { ...getErrorProps(errors['case_type']) }
                >
                  <Select onChange={ (value) => {
                    // Upper/Lower only applies to a single-arch case type —
                    // clear any stale value once it stops applying.
                    const stillSingleArch = types.find(t => t.value === value)?.key === 'singlearch';
                    if (! stillSingleArch) {
                      form.setFieldValue('arch_side', undefined);
                    }
                  } } options={ types } placeholder="Select case type" />
                </Form.Item>
              </Col>
            </Row>

            { isSingleArch &&
              <Row gutter={ 20 }>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item onChange={
                    () => setErrors([])
                  } name="arch_side" rules={ [{ required: true, message: 'Please select upper or lower arch!' }] } label="Which Arch :" className="da-mb-16"
                    { ...getErrorProps(errors['arch_side']) }
                  >
                    <Select options={ [
                      { label: 'Upper Arch', value: 'upper' },
                      { label: 'Lower Arch', value: 'lower' },
                    ] } placeholder="Select Upper or Lower Arch" />
                  </Form.Item>
                </Col>
              </Row>
            }

            <Row gutter={ 20 }>
              <Col xs={ 24 } md={ 12 }>
                <Form.Item onChange={
                  () => setErrors([])
                } name="gender" rules={ rules.gender } label="Gender :" className="da-mb-16"
                  { ...getErrorProps(errors['gender']) }
                >
                  <Select options={ [
                    { label: 'Male', value: 'male' },
                    { label: 'Female', value: 'female' },
                  ] } placeholder="Select Gender" />
                </Form.Item>
              </Col>
              <Col xs={ 24 } md={ 12 }>
                <Form.Item onChange={
                  () => setErrors([])
                } name="dob" label="Date of Birth :" className="da-mb-16 dob-field"
                  { ...getErrorProps(errors['dob']) }
                >
                  <DatePicker
                    style={ fullWidth }
                    type="date"
                    disabledDate={ isAfterDobLimit }
                    defaultPickerValue={ DOB_LIMIT.subtract(1, 'day') }
                    // The form lives in a drawer with its own scrollbar.
                    // Left in the body, the calendar is positioned once
                    // against the viewport and then stays there while the
                    // form scrolls underneath it, so it ends up floating
                    // over whatever happens to be beneath it. Rendered
                    // inside the field, it is anchored to the field and
                    // moves with it.
                    getPopupContainer={ (trigger) => trigger.parentElement }
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          { canAssignOwner &&
            <SectionCard icon="tabler:user-check" title="Case Ownership" subtitle="Assign this case to the doctor it belongs to">
              <Form.Item name="belongs_to_id" onChange={
                () => setErrors([])
              } rules={ rules.belongs_to_id } label="Assign Case To :" className="da-mb-16"
                { ...getErrorProps(errors['belongs_to_id']) }
              >
                <Select
                  options={ ownerOptions }
                  loading={ ownersLoading }
                  placeholder={ ownersLoading ? "Loading doctors…" : "Select the user this case belongs to" }
                />
              </Form.Item>
            </SectionCard>
          }
        </div>

        <div style={ {
          display: currentStep === 1 || showAll ? 'block' : 'none'
        } }>
          <SectionCard icon="tabler:stethoscope" title="Doctor’s Instructions">
            <Form.Item onChange={
              () => setErrors([])
            } name="doctor_instructions" rules={ rules.doctor_instructions } label="Doctor’s Instructions :" className="da-mb-16"
              { ...getErrorProps(errors['doctor_instructions']) }
            >
              <TextArea rows={ 4 } placeholder="Enter Doctor’s Instructions" />
            </Form.Item>
          </SectionCard>

          <SectionCard icon="tabler:report-medical" title="Clinical Assessment" subtitle="For each item, mark whether it should be improved or maintained — all optional, and only one at a time.">
            { clinicalFields.map(({ name, label }) => (
              <div key={ name } style={ { display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', padding: '8px 0' } }>
                <Text strong style={ { minWidth: 90 } }>{ label }</Text>
                <Form.Item noStyle onChange={
                  () => setErrors([])
                } name={ name } rules={ rules[name] }
                  { ...getErrorProps(errors[name]) }
                >
                  <ImproveMaintainCheckboxes />
                </Form.Item>
              </div>
            )) }
          </SectionCard>
        </div>

        <div style={ {
          display: currentStep === 2 || showAll ? 'block' : 'none'
        } }>
          { renderMediaSection({
            title: 'Photos', icon: 'tabler:camera', files: caseData?.photos,
            onUpload: onUploadPhoto, onDelete: deleteFile, type: 'photo', uploadText: 'Select Photos',
          }) }
          { renderMediaSection({
            title: 'X-ray', icon: 'tabler:radioactive', files: caseData?.xray,
            onUpload: onUploadXray, onDelete: deleteFile, type: 'xray', uploadText: 'Select X-ray',
          }) }
          { renderMediaSection({
            title: 'Scans', icon: 'tabler:3d-cube-sphere', files: caseData?.scans, image: 'scan',
            onUpload: onUploadScan, onDelete: deleteFile, type: 'scan', uploadText: 'Select Scans',
          }) }
        </div>

      </Form>

      <div className="form-actions">
        { currentStep === 3
          ? <>
            <CancelButton key="close_button" onClick={ () => onClosed(false) } />
            <SaveButton form={ formName } key="create_button" htmlType="submit" state={ loader } />
          </>
          : <>
            { currentStep > 0
              ? <ButtonComponent htmlType="button" onClick={ () => setCurrentStep(currentStep - 1) } type="default" state={ loader }>Back</ButtonComponent>
              : <CancelButton key="close_button" onClick={ () => onClosed(false) } /> }
            { currentStep < 3 &&
              <ButtonComponent htmlType="button" onClick={ () => setCurrentStep(currentStep + 1) } type="primary" state={ loader }>Next</ButtonComponent> }
          </> }
      </div>
    </>

  )
}

const rules = {
  name: [
    { required: true, message: 'Please input patient name!', },
    { min: 3, message: 'Minimum name length is 3', },
    { max: 100, message: 'Maximum name length is 100', },
  ],
  gender: [
    { required: true, message: 'Please select gender!', },
    { min: 3, message: 'Minimum name length is 3', },
    { max: 100, message: 'Maximum name length is 100', },
  ],
  dob: [
    { required: true, message: 'Please select dob!', },
    {
      validator: (_, value) => (isAfterDobLimit(value)
        ? Promise.reject(new Error('Date of birth must be before January 2021.'))
        : Promise.resolve()),
    },
  ],
  refine_id: [
    { required: true, message: 'Please select ref case!', },
  ],
  // No default: the doctor has to pick single arch or both arches.
  case_type: [
    { required: true, message: 'Please select case type!', },
  ],
  clinic_id: [
    { required: true, message: 'Please select clinic!', },
  ],
  // Optional: if left empty, the backend defaults the case owner to
  // whoever created it.
  belongs_to_id: [],
};


export default CaseJobForm;
